import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import Button from '@atlaskit/button'
import AddIcon from '@atlaskit/icon/glyph/add'
import BookIcon from '@atlaskit/icon/glyph/book'
import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs'
import {
  fetchLibraryFolderTreeAsync,
  fetchAllLibraryScenariosAsync,
  fetchLibraryScenariosInFolderAsync,
  deleteLibraryFolderAsync,
  deleteLibraryScenarioAsync,
  cloneLibraryScenarioAsync,
  reorderLibraryScenariosAsync,
  reorderLibraryScenariosOptimistic,
  reorderLibraryFoldersAsync,
  moveLibraryScenarioToFolderAsync,
  moveLibraryFolderAsync,
  selectLibraryFolderTree,
  selectLibraryFolders,
  selectLibraryScenarios,
  selectSelectedLibraryFolderId,
  selectLibraryFoldersLoading,
  selectLibraryScenariosLoading,
  selectLibraryError,
  selectLibraryFolder,
  clearLibrary,
  setExpandedLibraryFolderIds,
  selectExpandedLibraryFolderIds
} from '../../store/slices/librarySlice'
import { useNotification } from '../../contexts/NotificationContext'
import { setNavigationContext } from '../../store/slices/navigationContextSlice'
import { buildFolderPath } from '../../utils/folderUtils'
import { LibraryFolderSidebar } from './LibraryFolderSidebar'
import { LibraryScenarioPanel } from './LibraryScenarioPanel'
import { CreateLibraryFolderModal } from './CreateLibraryFolderModal'
import { CreateLibraryScenarioModal } from './CreateLibraryScenarioModal'
import { EditLibraryFolderModal } from './EditLibraryFolderModal'
import { EditLibraryScenarioModal } from './EditLibraryScenarioModal'
import { MoveLibraryScenarioModal } from './MoveLibraryScenarioModal'
import { ConfirmDeleteModal } from '../FolderBrowser/ConfirmDeleteModal'
import { FolderBrowserSkeleton } from '../common/FolderBrowserSkeleton'

/**
 * LIBRARY PAGE
 *
 * Central repository for reusable test scenario templates.
 * Library items have isLibrary=true and release=null.
 *
 * Layout:
 * - Header with title and "New Scenario" button
 * - Two-panel content:
 *   - Left: LibraryFolderSidebar (folder tree)
 *   - Right: LibraryScenarioPanel (scenarios)
 *
 * Features:
 * - Create/edit/delete library folders
 * - Create/edit/delete library scenarios
 * - Scenarios are templates (no test runs)
 * - DnD for reordering
 */
function Library() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { selectedProject } = useOutletContext()

  // Redux state
  const folderTree = useSelector(selectLibraryFolderTree)
  const folders = useSelector(selectLibraryFolders)
  const scenarios = useSelector(selectLibraryScenarios)
  const selectedFolderId = useSelector(selectSelectedLibraryFolderId)
  const selectedFolderIdRef = useRef(selectedFolderId)
  const expandedFolderIds = useSelector(selectExpandedLibraryFolderIds)
  const expandedFolderIdsRef = useRef(expandedFolderIds)
  const foldersLoading = useSelector(selectLibraryFoldersLoading)
  const scenariosLoading = useSelector(selectLibraryScenariosLoading)
  const error = useSelector(selectLibraryError)

  // Notification context for success/error messages
  const { showSuccess, showError } = useNotification()

  // =========================================================================
  // MODAL STATE
  // =========================================================================

  // Create modals
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false)
  const [isCreateScenarioModalOpen, setIsCreateScenarioModalOpen] = useState(false)
  const [createFolderParentId, setCreateFolderParentId] = useState(null)

  // Edit modals
  const [isEditFolderModalOpen, setIsEditFolderModalOpen] = useState(false)
  const [editingFolder, setEditingFolder] = useState(null)
  const [isEditScenarioModalOpen, setIsEditScenarioModalOpen] = useState(false)
  const [editingScenario, setEditingScenario] = useState(null)

  // Delete modals
  const [isDeleteFolderModalOpen, setIsDeleteFolderModalOpen] = useState(false)
  const [deletingFolder, setDeletingFolder] = useState(null)
  const [isDeleteScenarioModalOpen, setIsDeleteScenarioModalOpen] = useState(false)
  const [deletingScenario, setDeletingScenario] = useState(null)

  // Move modal
  const [isMoveScenarioModalOpen, setIsMoveScenarioModalOpen] = useState(false)
  const [movingScenario, setMovingScenario] = useState(null)

  // =========================================================================
  // DRAG AND DROP CONFIGURATION
  // =========================================================================

  // Configure drag sensors with activation distance to differentiate from clicks
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5 // 5px movement required before drag starts
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  /**
   * UNIFIED DRAG END HANDLER
   *
   * Handles all drag end events across the library browser:
   * 1. Scenario dropped on a folder → Move scenario to that folder
   * 2. Folder dropped on a folder → Move folder to become child of target folder
   * 3. Scenario reordering within panel → Reorder scenarios
   * 4. Folder reordering in sidebar → Reorder folders
   */
  const handleUnifiedDragEnd = (event) => {
    const { active, over } = event

    if (!over) return

    const activeId = String(active.id)
    const overId = String(over.id)

    // Helper to check if dragged item is a scenario
    const isScenarioDrag = activeId.startsWith('scenario-')
    // Helper to check if dragged item is a folder
    const isFolderDrag = activeId.startsWith('folder-') && !activeId.startsWith('folder-drop-')
    // Helper to check if drop target is a folder drop zone
    const isFolderDropTarget = overId.startsWith('folder-drop-')

    // Helper to determine drop position: top/bottom edge (30%) vs center (40%)
    // Returns 'top', 'bottom', or 'center' to support both reordering and folder promotion
    const getDropIntent = () => {
      if (!over.rect || !event.activatorEvent) return 'center'

      const overRect = over.rect
      const pointerY = event.activatorEvent.clientY + (event.delta?.y || 0)

      const relativeY = pointerY - overRect.top
      const percentY = relativeY / overRect.height

      // Top 30% = insert before, bottom 30% = insert after, center 40% = nest
      if (percentY < 0.3) return 'top'
      if (percentY > 0.7) return 'bottom'
      return 'center'
    }

    // =========================================================================
    // CASE 0: Scenario dropped on "All Scenarios" → Clear folder (move to root)
    // =========================================================================
    if (isScenarioDrag && overId === 'all-scenarios-drop') {
      const scenarioId = activeId.replace('scenario-', '')
      const draggedScenario = scenarios.find(s => String(s.id) === scenarioId)

      if (!draggedScenario) return

      // No-op if scenario is already at root
      if (draggedScenario.folderId == null) return

      dispatch(moveLibraryScenarioToFolderAsync({
        scenarioId: parseInt(scenarioId, 10),
        moveData: { folderId: null }
      })).unwrap().then(() => {
        // Refresh scenarios for the current view (folder or all)
        if (selectedFolderId !== null) {
          dispatch(fetchLibraryScenariosInFolderAsync(selectedFolderId))
        } else if (selectedProject?.value) {
          dispatch(fetchAllLibraryScenariosAsync(selectedProject.value))
        }
        // Refresh folder tree to update scenario counts
        if (selectedProject?.value) {
          dispatch(fetchLibraryFolderTreeAsync(selectedProject.value))
        }
      }).catch((error) => {
        const message = error?.message || 'Failed to move scenario. Please try again.'
        showError('Move Failed', message)
        if (selectedProject?.value) {
          dispatch(fetchLibraryFolderTreeAsync(selectedProject.value))
        }
      })
      return
    }

    // =========================================================================
    // CASE 1: Scenario dropped on a folder → Move scenario to that folder
    // =========================================================================
    if (isScenarioDrag && isFolderDropTarget) {
      const scenarioId = activeId.replace('scenario-', '')
      const targetFolderId = overId.replace('folder-drop-', '')

      const draggedScenario = scenarios.find(s => String(s.id) === scenarioId)

      if (draggedScenario && targetFolderId) {
        if (String(draggedScenario.folderId) === targetFolderId) {
          return
        }

        dispatch(moveLibraryScenarioToFolderAsync({
          scenarioId: parseInt(scenarioId, 10),
          moveData: {
            folderId: parseInt(targetFolderId, 10)
          }
        })).then(() => {
          if (selectedFolderId !== null) {
            dispatch(fetchLibraryScenariosInFolderAsync(selectedFolderId))
          }
          if (selectedProject?.value) {
            dispatch(fetchLibraryFolderTreeAsync(selectedProject.value))
          }
        })
      }
      return
    }

    // =========================================================================
    // CASE 2: Folder dropped on another folder → Move or Reorder
    // =========================================================================
    if (isFolderDrag && isFolderDropTarget) {
      const draggedFolderId = activeId.replace('folder-', '')
      const targetFolderId = overId.replace('folder-drop-', '')

      if (draggedFolderId === targetFolderId) {
        return
      }

      const draggedFolder = folders.find(f => String(f.id) === draggedFolderId)
      const targetFolder = folders.find(f => String(f.id) === targetFolderId)

      if (!draggedFolder || !targetFolder) return

      const dropIntent = getDropIntent()

      if (dropIntent === 'top' || dropIntent === 'bottom') {
        const sameParent = String(draggedFolder.parentId || '') === String(targetFolder.parentId || '')

        if (sameParent) {
          // Same parent → reorder siblings
          const parentId = draggedFolder.parentId
          let siblings
          if (parentId === null) {
            siblings = folderTree
          } else {
            const parentFolder = folders.find(f => f.id === parentId)
            siblings = parentFolder?.children || []
          }

          const oldIndex = siblings.findIndex(f => String(f.id) === draggedFolderId)
          const newIndex = siblings.findIndex(f => String(f.id) === targetFolderId)

          if (oldIndex !== -1 && newIndex !== -1) {
            // Send only the dragged folder's new 1-based orderIndex — the backend
            // places it at that position and reindexes every sibling to a
            // contiguous 1..N. Sending the full reordered list via parallel
            // Promise.all calls (the previous implementation) let the backend
            // interleave updates and produce duplicate orderIndex values.
            dispatch(reorderLibraryFoldersAsync([{
              id: draggedFolder.id,
              orderIndex: newIndex + 1
            }])).then(() => {
              if (selectedProject?.value) {
                dispatch(fetchLibraryFolderTreeAsync(selectedProject.value))
              }
            })
          }
          return
        } else {
          // Different parent → move folder to the target's parent level
          // This enables promoting a subfolder by dragging it to the edge of a folder at a higher level
          const newParentId = targetFolder.parentId

          // Prevent moving a folder into its own descendant's parent (circular move)
          const isDescendantCheck = (folderId, targetId) => {
            const children = folders.filter(f => String(f.parentId) === folderId)
            for (const child of children) {
              if (String(child.id) === targetId) return true
              if (isDescendantCheck(String(child.id), targetId)) return true
            }
            return false
          }

          if (isDescendantCheck(draggedFolderId, targetFolderId)) {
            return
          }

          // Find target's siblings at the destination level to calculate orderIndex
          let destinationSiblings
          if (newParentId === null) {
            destinationSiblings = folderTree
          } else {
            const parentFolder = folders.find(f => f.id === newParentId)
            destinationSiblings = parentFolder?.children || []
          }

          // Calculate insertion index based on drop edge position
          const targetIndex = destinationSiblings.findIndex(f => String(f.id) === targetFolderId)
          const orderIndex = dropIntent === 'bottom' ? targetIndex + 2 : targetIndex + 1

          dispatch(moveLibraryFolderAsync({
            folderId: parseInt(draggedFolderId, 10),
            moveData: {
              newParentId: newParentId !== null ? parseInt(String(newParentId), 10) : null,
              orderIndex: Math.max(1, orderIndex)
            }
          })).then(() => {
            if (selectedProject?.value) {
              dispatch(fetchLibraryFolderTreeAsync(selectedProject.value))
            }
          })
          return
        }
      }

      // Nest folder (center drop zone)
      if (String(draggedFolder.parentId) === targetFolderId) {
        return
      }

      const isDescendant = (folderId, targetId) => {
        const children = folders.filter(f => String(f.parentId) === folderId)
        for (const child of children) {
          if (String(child.id) === targetId) return true
          if (isDescendant(String(child.id), targetId)) return true
        }
        return false
      }

      if (isDescendant(draggedFolderId, targetFolderId)) {
        return
      }

      dispatch(moveLibraryFolderAsync({
        folderId: parseInt(draggedFolderId, 10),
        moveData: {
          newParentId: parseInt(targetFolderId, 10),
          orderIndex: 1 // 1-based to match backend
        }
      })).then(() => {
        if (selectedProject?.value) {
          dispatch(fetchLibraryFolderTreeAsync(selectedProject.value))
        }
      })
      return
    }

    // =========================================================================
    // CASE 3: Scenario reordering within the same panel
    // =========================================================================
    if (isScenarioDrag && overId.startsWith('scenario-')) {
      const activeScenarioId = activeId.replace('scenario-', '')
      const overScenarioId = overId.replace('scenario-', '')

      if (activeScenarioId !== overScenarioId) {
        const oldIndex = scenarios.findIndex(s => String(s.id) === activeScenarioId)
        const newIndex = scenarios.findIndex(s => String(s.id) === overScenarioId)

        if (oldIndex !== -1 && newIndex !== -1) {
          const reorderedScenarios = [...scenarios]
          const [removed] = reorderedScenarios.splice(oldIndex, 1)
          reorderedScenarios.splice(newIndex, 0, removed)

          const reorderData = reorderedScenarios.map((scenario, index) => ({
            id: scenario.id,
            orderIndex: index + 1
          }))

          dispatch(reorderLibraryScenariosOptimistic(reorderData))

          const draggedScenario = scenarios.find(s => String(s.id) === activeScenarioId)
          dispatch(reorderLibraryScenariosAsync([{
            id: draggedScenario.id,
            orderIndex: newIndex + 1
          }]))
        }
      }
      return
    }

    // =========================================================================
    // CASE 4: Folder reordering within sidebar (same level)
    // =========================================================================
    if (isFolderDrag && overId.startsWith('folder-') && !overId.startsWith('folder-drop-')) {
      const activeFolderId = activeId.replace('folder-', '')
      const overFolderId = overId.replace('folder-', '')

      if (activeFolderId !== overFolderId) {
        const oldIndex = folderTree.findIndex(f => String(f.id) === activeFolderId)
        const newIndex = folderTree.findIndex(f => String(f.id) === overFolderId)

        if (oldIndex !== -1 && newIndex !== -1) {
          const reorderedFolders = [...folderTree]
          const [removed] = reorderedFolders.splice(oldIndex, 1)
          reorderedFolders.splice(newIndex, 0, removed)

          const reorderData = reorderedFolders.map((folder, index) => ({
            id: folder.id,
            orderIndex: index + 1
          }))

          dispatch(reorderLibraryFoldersAsync(reorderData)).then(() => {
            if (selectedProject?.value) {
              dispatch(fetchLibraryFolderTreeAsync(selectedProject.value))
            }
          })
        }
      }
      return
    }
  }

  // =========================================================================
  // DATA FETCHING
  // =========================================================================

  // Fetch library data when project changes
  useEffect(() => {
    if (selectedProject?.value) {
      dispatch(fetchLibraryFolderTreeAsync(selectedProject.value))
      dispatch(fetchAllLibraryScenariosAsync(selectedProject.value))
    }

    return () => {
      dispatch(clearLibrary())
    }
  }, [dispatch, selectedProject?.value])

  // =========================================================================
  // FOLDER EXPANSION STATE PERSISTENCE
  // =========================================================================

  // Restore expanded folder state from localStorage
  useEffect(() => {
    if (selectedProject?.value) {
      const storageKey = `expanded-library-folders-${selectedProject.value}`
      const savedExpandedIds = localStorage.getItem(storageKey)
      if (savedExpandedIds) {
        try {
          const parsed = JSON.parse(savedExpandedIds)
          if (Array.isArray(parsed)) {
            dispatch(setExpandedLibraryFolderIds(parsed))
          }
        } catch (e) {
          console.warn('Failed to parse saved folder expansion state:', e)
        }
      } else {
        dispatch(setExpandedLibraryFolderIds([]))
      }
    }
  }, [selectedProject?.value, dispatch])

  // Save expanded folder state to localStorage when it changes
  useEffect(() => {
    if (selectedProject?.value && expandedFolderIds) {
      const storageKey = `expanded-library-folders-${selectedProject.value}`
      localStorage.setItem(storageKey, JSON.stringify(expandedFolderIds))
    }
  }, [selectedProject?.value, expandedFolderIds])

  // =========================================================================
  // URL QUERY PARAMETER SYNC
  // =========================================================================

  // Keep refs in sync with latest Redux state so the URL sync effect
  // can read current values without re-triggering on every state change
  useEffect(() => {
    selectedFolderIdRef.current = selectedFolderId
  }, [selectedFolderId])

  useEffect(() => {
    expandedFolderIdsRef.current = expandedFolderIds
  }, [expandedFolderIds])

  /**
   * Sync folder selection FROM URL query parameter on mount/URL change.
   *
   * Reads ?folder={id} from URL and:
   * 1. Validates the folder exists in the loaded folder tree
   * 2. Selects the folder in Redux state
   * 3. Auto-expands parent folders for nested folder deep-links
   * 4. Removes invalid ?folder param gracefully
   *
   * This enables breadcrumb navigation back to a specific library folder.
   */
  useEffect(() => {
    // Wait until folders are loaded before syncing
    if (foldersLoading || folders.length === 0) {
      return
    }

    const folderIdParam = searchParams.get('folder')

    // No folder param in URL — this is the "All Scenarios" view.
    // Align Redux if it still points at a previously-selected folder, otherwise
    // the sidebar row wouldn't get the selected styling and the panel wouldn't switch.
    if (!folderIdParam) {
      if (selectedFolderIdRef.current !== null) {
        dispatch(selectLibraryFolder(null))
      }
      return
    }

    const folderId = parseInt(folderIdParam, 10)

    // Validate folder exists
    const folderExists = folders.some(f => f.id === folderId)

    if (!folderExists) {
      // Invalid folder ID - remove param and log warning
      console.warn(`Library folder ID ${folderId} from URL not found. Removing param.`)
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('folder')
      setSearchParams(newParams, { replace: true })
      return
    }

    // Folder exists - select it if not already selected
    if (selectedFolderIdRef.current !== folderId) {
      dispatch(selectLibraryFolder(folderId))

      // Auto-expand parent folders for nested folder deep-links
      const folderPath = buildFolderPath(folderId, folders)
      if (folderPath.length > 1) {
        // Get all ancestor folder IDs (excluding the current folder)
        const ancestorIds = folderPath.slice(0, -1).map(f => f.id)
        // Merge with existing expanded folders
        const currentExpanded = expandedFolderIdsRef.current || []
        const newExpandedIds = [...new Set([...currentExpanded, ...ancestorIds])]
        if (newExpandedIds.length !== currentExpanded.length) {
          dispatch(setExpandedLibraryFolderIds(newExpandedIds))
        }
      }
    }
  }, [searchParams, folders, foldersLoading, dispatch, setSearchParams])

  // Fetch scenarios when folder selection changes. When selectedFolderId is null
  // we're in the "All Scenarios" view and must refetch the project-wide library
  // list — otherwise state.libraryScenarios still holds the previously-selected folder's list.
  useEffect(() => {
    if (selectedFolderId !== null) {
      dispatch(fetchLibraryScenariosInFolderAsync(selectedFolderId))
    } else if (selectedProject?.value) {
      dispatch(fetchAllLibraryScenariosAsync(selectedProject.value))
    }
  }, [dispatch, selectedFolderId, selectedProject?.value])

  // =========================================================================
  // NAVIGATION HANDLERS
  // =========================================================================

  // Build folder path for breadcrumb display
  const folderPath = buildFolderPath(selectedFolderId, folders)

  // Navigate to library root when project breadcrumb is clicked
  const handleProjectClick = (e) => {
    e.preventDefault()
    navigate('/library')
  }

  // Navigate to library root (resets to "All Scenarios" view)
  const handleLibraryClick = (e) => {
    e.preventDefault()
    navigate('/library')
  }

  // Navigate to a specific folder via breadcrumb click
  const handleFolderBreadcrumbClick = (e, folderId) => {
    e.preventDefault()
    const newParams = new URLSearchParams(searchParams)
    newParams.set('folder', String(folderId))
    setSearchParams(newParams, { replace: true })
  }

  /**
   * Handle folder selection from sidebar.
   * Only updates the URL query parameter — the URL sync effect
   * is the single source of truth for dispatching selectLibraryFolder(),
   * which prevents duplicate API calls.
   */
  const handleFolderSelect = (folderId) => {
    // Only update URL - the URL sync effect will handle Redux state update.
    // folderId === null means the "All Scenarios" view; remove the param entirely.
    const newParams = new URLSearchParams(searchParams)
    if (folderId === null || folderId === undefined) {
      newParams.delete('folder')
    } else {
      newParams.set('folder', String(folderId))
    }
    setSearchParams(newParams, { replace: true })
  }

  // Handle scenario click - navigate to library scenario detail
  // Stores navigation context for breadcrumb display in HeaderSection
  const handleScenarioClick = (scenarioId) => {
    // Build folder path for breadcrumb display using flat library folders list
    const folderPath = buildFolderPath(selectedFolderId, folders)

    // Store navigation context for library scenario
    dispatch(setNavigationContext({
      isLibrary: true,
      currentRelease: null,
      folderPath: folderPath
    }))

    navigate(`/library/scenario/${scenarioId}`)
  }

  // =========================================================================
  // FOLDER CRUD HANDLERS
  // =========================================================================

  const handleCreateFolder = (parentId = null) => {
    setCreateFolderParentId(parentId)
    setIsCreateFolderModalOpen(true)
  }

  const handleEditFolder = (folderId) => {
    const folder = folders.find(f => f.id === folderId)
    if (folder) {
      setEditingFolder(folder)
      setIsEditFolderModalOpen(true)
    }
  }

  const handleDeleteFolder = (folderId) => {
    const folder = folders.find(f => f.id === folderId)
    if (folder) {
      setDeletingFolder(folder)
      setIsDeleteFolderModalOpen(true)
    }
  }

  const handleConfirmDeleteFolder = async () => {
    if (deletingFolder?.id) {
      await dispatch(deleteLibraryFolderAsync(deletingFolder.id)).unwrap()
    }
  }

  // =========================================================================
  // SCENARIO CRUD HANDLERS
  // =========================================================================

  const handleCreateScenario = () => {
    setIsCreateScenarioModalOpen(true)
  }

  const handleEditScenario = (scenarioId) => {
    const scenario = scenarios.find(s => s.id === scenarioId)
    if (scenario) {
      setEditingScenario(scenario)
      setIsEditScenarioModalOpen(true)
    }
  }

  const handleDeleteScenario = (scenarioId) => {
    const scenario = scenarios.find(s => s.id === scenarioId)
    if (scenario) {
      setDeletingScenario(scenario)
      setIsDeleteScenarioModalOpen(true)
    }
  }

  const handleConfirmDeleteScenario = async () => {
    if (deletingScenario?.id) {
      await dispatch(deleteLibraryScenarioAsync(deletingScenario.id)).unwrap()
    }
  }

  const handleMoveScenario = (scenarioId) => {
    const scenario = scenarios.find(s => s.id === scenarioId)
    if (scenario) {
      setMovingScenario(scenario)
      setIsMoveScenarioModalOpen(true)
    }
  }

  const handleCloneScenario = async (scenarioId) => {
    try {
      await dispatch(cloneLibraryScenarioAsync(scenarioId)).unwrap()
      // Refresh scenarios so the cloned row appears immediately (matches release UX).
      // The reducer also pushes the clone into state, but folderId shape mismatches
      // can hide it from the current view — re-fetching is the reliable fix.
      if (selectedFolderId !== null) {
        dispatch(fetchLibraryScenariosInFolderAsync(selectedFolderId))
      } else if (selectedProject?.value) {
        dispatch(fetchAllLibraryScenariosAsync(selectedProject.value))
      }
      showSuccess('Scenario Cloned', 'Library scenario has been cloned successfully.')
    } catch (error) {
      showError('Clone Failed', error?.message || 'Failed to clone library scenario.')
    }
  }

  // =========================================================================
  // REORDER HANDLERS
  // =========================================================================

  const handleReorderScenarios = (reorderData) => {
    dispatch(reorderLibraryScenariosOptimistic(reorderData))
    dispatch(reorderLibraryScenariosAsync(reorderData))
  }

  const handleReorderFolders = (reorderData) => {
    dispatch(reorderLibraryFoldersAsync(reorderData)).then(() => {
      if (selectedProject?.value) {
        dispatch(fetchLibraryFolderTreeAsync(selectedProject.value))
      }
    })
  }

  // =========================================================================
  // RENDER
  // =========================================================================

  // No project selected state
  if (!selectedProject?.value) {
    return (
      <div
        className="library-page"
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--ds-surface, #FFFFFF)'
        }}
      >
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          backgroundColor: 'var(--ds-background-neutral, #F4F5F7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '24px'
        }}>
          <BookIcon label="Library" size="large" primaryColor="var(--ds-text-subtlest, #6B778C)" />
        </div>

        <h2 style={{
          fontSize: '20px',
          fontWeight: 500,
          color: 'var(--ds-text, #172B4D)',
          margin: '0 0 8px 0'
        }}>
          Select a project
        </h2>

        <p style={{
          fontSize: '14px',
          color: 'var(--ds-text-subtlest, #6B778C)',
          margin: 0,
          maxWidth: '400px',
          textAlign: 'center'
        }}>
          Choose a project from the sidebar to view its library of reusable test scenario templates.
        </p>
      </div>
    )
  }

  // Loading state - shimmer skeleton mimicking the full two-panel layout
  if (foldersLoading && folderTree.length === 0) {
    return <FolderBrowserSkeleton />
  }

  // Error state
  if (error) {
    return (
      <div
        className="library-page"
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--ds-surface, #FFFFFF)'
        }}
      >
        <p style={{ color: 'var(--ds-text-danger, #DE350B)' }}>Error loading library</p>
        <p style={{ color: 'var(--ds-text-subtlest, #6B778C)', fontSize: '14px' }}>{error}</p>
        <Button
          appearance="primary"
          onClick={() => {
            if (selectedProject?.value) {
              dispatch(fetchLibraryFolderTreeAsync(selectedProject.value))
              dispatch(fetchAllLibraryScenariosAsync(selectedProject.value))
            }
          }}
          style={{ marginTop: '16px' }}
        >
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div
      className="library-page"
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--ds-surface, #FFFFFF)',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <div
        className="page-header"
        style={{
          padding: '8px 20px',
          borderBottom: '1px solid var(--ds-border, #DFE1E6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        {/* Breadcrumb Navigation */}
        <Breadcrumbs>
          <BreadcrumbsItem
            text={selectedProject?.key || 'Project'}
            onClick={handleProjectClick}
          />
          <BreadcrumbsItem
            text="Library"
            onClick={handleLibraryClick}
          />
          {folderPath.map((folder, index) => (
            <BreadcrumbsItem
              key={folder.id}
              text={folder.name}
              onClick={index < folderPath.length - 1
                ? (e) => handleFolderBreadcrumbClick(e, folder.id)
                : undefined}
            />
          ))}
        </Breadcrumbs>

        {/* Action Button */}
        <Button
          appearance="primary"
          iconBefore={<AddIcon label="Add scenario" size="small" />}
          onClick={handleCreateScenario}
        >
          New Scenario
        </Button>
      </div>

      {/* Two-Panel Layout with Unified Drag Context */}
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragEnd={handleUnifiedDragEnd}
      >
        {/* Blue background wrapper - matches FolderBrowser pattern */}
        <div className="folder-browser-wrapper">
          {/* White rounded card container for sidebar + scenario panel */}
          <div className="folder-browser-content">
          {/* Left Panel - Folder Sidebar */}
          <LibraryFolderSidebar
            folderTree={folderTree}
            selectedFolderId={selectedFolderId}
            onFolderSelect={handleFolderSelect}
            onAddSubfolder={handleCreateFolder}
            onRenameFolder={handleEditFolder}
            onDeleteFolder={handleDeleteFolder}
            onReorderFolders={handleReorderFolders}
            jiraProjectId={selectedProject?.value}
          />

          {/* Right Panel - Scenarios */}
          <LibraryScenarioPanel
            scenarios={scenarios}
            selectedFolderId={selectedFolderId}
            loading={scenariosLoading}
            onScenarioClick={handleScenarioClick}
            onCreateScenario={handleCreateScenario}
            onEditScenario={handleEditScenario}
            onDeleteScenario={handleDeleteScenario}
            onMoveScenario={handleMoveScenario}
            onCloneScenario={handleCloneScenario}
            onReorderScenarios={handleReorderScenarios}
            jiraProjectId={selectedProject?.value}
          />
          </div>
        </div>
      </DndContext>

      {/* ===================================================================
          MODALS
          =================================================================== */}

      {/* Create Folder Modal */}
      <CreateLibraryFolderModal
        isOpen={isCreateFolderModalOpen}
        onClose={() => {
          setIsCreateFolderModalOpen(false)
          setCreateFolderParentId(null)
        }}
        parentFolderId={createFolderParentId}
        jiraProjectId={selectedProject?.value}
      />

      {/* Create Scenario Modal */}
      <CreateLibraryScenarioModal
        isOpen={isCreateScenarioModalOpen}
        onClose={() => setIsCreateScenarioModalOpen(false)}
        folderId={selectedFolderId}
        jiraProjectId={selectedProject?.value}
      />

      {/* Edit Folder Modal */}
      <EditLibraryFolderModal
        isOpen={isEditFolderModalOpen}
        onClose={() => {
          setIsEditFolderModalOpen(false)
          setEditingFolder(null)
        }}
        folder={editingFolder}
      />

      {/* Edit Scenario Modal */}
      <EditLibraryScenarioModal
        isOpen={isEditScenarioModalOpen}
        onClose={() => {
          setIsEditScenarioModalOpen(false)
          setEditingScenario(null)
        }}
        scenario={editingScenario}
      />

      {/* Delete Folder Confirmation */}
      <ConfirmDeleteModal
        isOpen={isDeleteFolderModalOpen}
        onClose={() => {
          setIsDeleteFolderModalOpen(false)
          setDeletingFolder(null)
        }}
        onConfirm={handleConfirmDeleteFolder}
        title="Delete Library Folder"
        itemName={deletingFolder?.name || 'folder'}
        itemType="folder"
      />

      {/* Delete Scenario Confirmation */}
      <ConfirmDeleteModal
        isOpen={isDeleteScenarioModalOpen}
        onClose={() => {
          setIsDeleteScenarioModalOpen(false)
          setDeletingScenario(null)
        }}
        onConfirm={handleConfirmDeleteScenario}
        title="Delete Library Scenario"
        itemName={deletingScenario?.scenarioName || 'scenario'}
        itemType="scenario"
      />

      {/* Move Scenario Modal */}
      <MoveLibraryScenarioModal
        isOpen={isMoveScenarioModalOpen}
        onClose={() => {
          setIsMoveScenarioModalOpen(false)
          setMovingScenario(null)
        }}
        scenario={movingScenario}
        currentFolderId={selectedFolderId}
      />
    </div>
  )
}

export { Library }
